import { KeyValuePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NIFTY_APPLICATIONID_KEY, ServiceType } from '@app/app.constants';
import { LayoutService } from '@app/modules/main/services/layout.service';
import {
  SampleType,
  sampleTypeKey,
} from '@app/modules/service-data/types/sample.types';
import {
  Gender,
  PregnancyType,
} from '@app/modules/service-data/types/service-data.types';
import {
  AnalysisCode,
  AnalysisType,
  DataInputType,
  Ident,
  IncidentalFindingsReporting,
  ModalMode,
  Product,
  ProductAnalysisCodeModel,
} from '@core/core.types';
import { ProductUtilsService } from '@core/services/product-utils.service';
import { AnalysisCodesState } from '@core/store/analysis-codes/analysis-codes.state';
import { PantheonState } from '@core/store/pantheon/pantheon.state';
import {
  AddChildProducts,
  AddProductAction,
  LoadProductAction,
  RemoveChildProducts,
  UpdateProductAction,
} from '@core/store/products/products.actions';
import { ProductsListState } from '@core/store/products/products.state';
import { SchemeState } from '@core/store/schemes/schemes.state';
import { TemplatesState } from '@core/store/templates/templates.state';
import { Util } from '@core/utils/core.util';
import { NgbModal, NgbTooltip, NgbTypeahead } from '@ng-bootstrap/ng-bootstrap';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { ConfirmService } from '@shared/components/confirm/confirm-dialog.component';
import { MultiSelectCheckboxComponent } from '@shared/components/multi-select/multi-select-checkbox/multi-select-checkbox.component';
import { ProgressDialogComponent } from '@shared/components/progress/progress-dialog.component';
import { SelectOptionComponent } from '@shared/components/select/select-option/select-option.component';
import { SelectComponent } from '@shared/components/select/select.component';
import { Laboratory } from '@shared/constants/kit.constants';
import { ConfirmCloseReason } from '@shared/shared.types';
import { forkJoin, map, Observable, OperatorFunction } from 'rxjs';
import { debounceTime, delay, startWith, switchMap } from 'rxjs/operators';
import { MultiSelectComponent } from '../../../@shared/components/multi-select/multi-select.component';
import { DisplayNamePipe } from '../../../@shared/pipes/display-name.pipe';
import { ConfigurationTypeaheadComponent } from '../../configuration-typeahead/configuration-typeahead.component';

@Component({
  selector: 'app-product-form',
  templateUrl: './product-form.component.html',
  styleUrls: ['./product-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgbTooltip,
    FormsModule,
    ReactiveFormsModule,
    NgbTypeahead,
    KeyValuePipe,
    DisplayNamePipe,
    ConfigurationTypeaheadComponent,
    SelectComponent,
    SelectOptionComponent,
    MultiSelectComponent,
    MultiSelectCheckboxComponent,
    TranslatePipe,
  ],
})
export class ProductFormComponent implements OnInit {
  protected layoutService = inject(LayoutService);
  private route = inject(ActivatedRoute);
  private store = inject(Store);
  private ngbModal = inject(NgbModal);
  private router = inject(Router);
  private confirmService = inject(ConfirmService);
  private productUtilsService = inject(ProductUtilsService);
  private destroyRef = inject(DestroyRef);
  private translateService = inject(TranslateService);
  protected id = input('id');
  private idents = this.store.selectSignal(PantheonState.getIdentsList);
  protected products = this.store.selectSignal(
    ProductsListState.getProductsList
  );
  protected activeProducts = this.store.selectSignal(
    ProductsListState.getActiveProductsList
  );
  protected getCodesForLaboratory = this.store.selectSignal(
    AnalysisCodesState.getCodesForLaboratory
  );

  additionalFields = signal([]);
  protected sampleTypeOptions = Object.values(SampleType);
  protected laboratories = Object.values(Laboratory);
  protected availableLaboratories = signal<string[]>(this.laboratories);

  initialchildProductIds: string[] = [];

  mode = ModalMode.Create;

  ServiceType = ServiceType;
  ModalMode = ModalMode;
  PregnancyType = PregnancyType;

  valuesChanged = false;

  form = new FormGroup({
    id: new FormControl<string>(Util.CreateGuid(), Validators.required),
    name: new FormControl<string>('', Validators.required),
    brand: new FormControl<string>('', Validators.required),
    childProductIds: new FormControl<string[]>([]),
    internalName: new FormControl<string>('', Validators.required),
    schemeId: new FormControl<string>('', Validators.required),
    templateId: new FormControl<string>('', Validators.required),
    externalId: new FormControl<Ident>(null),
    externalId2: new FormControl<Ident>(null),
    externalId3: new FormControl<Ident>(null),
    incidentalFindings: new FormControl<boolean>(false),
    incidentalFindingsReporting: new FormControl<IncidentalFindingsReporting>(
      null
    ),
    sex: new FormControl<Gender[]>([]),
    allowedSampleTypes: new FormControl<string[]>([]),
    requirePatientAddress: new FormControl<boolean>(false),
    productSubTypes: new FormControl<string[]>([]),
    analysisType: new FormControl<AnalysisType>(null, Validators.required),
    dataInputType: new FormControl<DataInputType>(null, Validators.required),
    analysisProductCodes: new FormControl<ProductAnalysisCodeModel[]>([]),
    applicationId: new FormControl<string>(
      NIFTY_APPLICATIONID_KEY,
      Validators.required
    ),
  });
  analysisProductCodeLab = new FormControl<string>('');
  analysisProductCodeValue = new FormControl<string>('');

  protected analysisProductCodeOptions = signal<AnalysisCode[]>([]);

  allSchemes = this.store.selectSignal(SchemeState.getActiveSchemes);
  inputType = toSignal(this.form.get('analysisType').valueChanges);
  schemes = computed(() =>
    this.allSchemes().filter(
      (scheme) => scheme.analysisType === this.inputType()
    )
  );
  templates = this.store.selectSignal(TemplatesState.getActiveTemplates);

  product = computed(
    () =>
      this.products().find((product) => product.id === this.id()) ||
      ({} as Product)
  );

  childProducts = toSignal(this.form.get('childProductIds').valueChanges);
  filteredProducts = computed(() =>
    this.activeProducts().filter(
      (product) => !this.childProducts()?.includes(product.id)
    )
  );

  constructor() {
    effect(() => {
      if (this.product()?.name) {
        this.mode = ModalMode.Edit;
        this.initialchildProductIds = this.product().childProductIds;

        this.form.patchValue({
          ...this.product(),
          externalId: this.idents().find(
            (ident) => ident.ident === this.product().externalId
          ),
          externalId2: this.idents().find(
            (ident) => ident.ident === this.product().externalId2
          ),
          externalId3: this.idents().find(
            (ident) => ident.ident === this.product().externalId3
          ),
        });
      }
    });

    effect(() => {});
  }

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.valuesChanged = true;
      });

    this.analysisProductCodeLab.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((lab) => {
        this.analysisProductCodeValue.setValue('');
        if (!lab) {
          this.analysisProductCodeOptions.set([]);
          return;
        }
        this.analysisProductCodeOptions.set(
          this.getCodesForLaboratory()(lab) || []
        );

        if (this.analysisProductCodeOptions().length === 0) {
          this.analysisProductCodeValue.disable();
        } else {
          this.analysisProductCodeValue.enable();
        }
      });

    this.form
      .get('analysisProductCodes')
      .valueChanges.pipe(
        startWith(this.form.get('analysisProductCodes').value),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.availableLaboratories.set(this.getAvailableLaboratories());
      });

    this.form
      .get('dataInputType')
      .valueChanges.pipe(
        startWith(this.form.get('dataInputType').value),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((dataInputType) => {
        const incidentalFindingsControl = this.form.get(
          'incidentalFindingsReporting'
        );
        incidentalFindingsControl.clearValidators();

        const sexControl = this.form.get('sex');
        sexControl.clearValidators();

        const productSubTypesControl = this.form.get('productSubTypes');
        productSubTypesControl.clearValidators();

        const sampleTypesControl = this.form.get('allowedSampleTypes');
        sampleTypesControl.clearValidators();

        this.additionalFields.set(
          this.productUtilsService.productAdditionalFields(dataInputType)
        );

        if (
          !this.additionalFields().includes(ProductAdditionalFields.SampleTypes)
        ) {
          sampleTypesControl.setValue([]);
        }

        if (
          !this.additionalFields().includes(
            ProductAdditionalFields.IncidentalFindings
          )
        ) {
          incidentalFindingsControl.setValue(null);
        }

        if (
          !this.additionalFields().includes(
            ProductAdditionalFields.ProductSubTypes
          )
        ) {
          productSubTypesControl.setValue([]);
        }

        if (!this.additionalFields().includes(ProductAdditionalFields.Sex)) {
          sexControl.setValue([]);
        }

        if (
          !this.additionalFields().includes(
            ProductAdditionalFields.RequirePatientAddress
          )
        ) {
          this.form.get('requirePatientAddress').setValue(false);
        }

        for (const field of this.additionalFields()) {
          if (field === ProductAdditionalFields.RequirePatientAddress) {
            continue;
          }
          if (this.form.get(field)) {
            this.form.get(field).setValidators(Validators.required);
          }
        }

        // Validators changed, so the old validity (e.g. a required error from
        // the previous data input type) must be recalculated
        for (const control of [
          incidentalFindingsControl,
          sexControl,
          productSubTypesControl,
          sampleTypesControl,
        ]) {
          control.updateValueAndValidity({ emitEvent: false });
        }
      });
  }

  c(name: string) {
    return this.form.get(name);
  }

  onProductSelection(productId: string) {
    if (productId) {
      this.c('childProductIds').setValue(
        [...this.c('childProductIds').value, productId].filter(
          (item, index, arr) => arr.indexOf(item) === index
        )
      );

      this.valuesChanged = true;
    }
  }

  removeProduct(productId) {
    this.c('childProductIds').setValue([
      ...this.c('childProductIds').value.filter((item) => item !== productId),
    ]);
  }

  save() {
    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    const dialogRef = this.ngbModal.open(ProgressDialogComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false,
      modalDialogClass: 'modal-size-md',
    });
    dialogRef.componentInstance.title = this.translateService.instant(
      'configurationPage.products.form.progress.saving'
    );

    const formValue = this.form.getRawValue();
    const product: Product = {
      ...this.form.getRawValue(),
      externalId: formValue.externalId?.ident || null,
      externalId2: formValue.externalId2?.ident || null,
      externalId3: formValue.externalId3?.ident || null,
      properties: [{ itemKey: 'Brand', itemValue: formValue.brand }],
    };

    const removeProducts = this.initialchildProductIds.filter(
      (pid) => !formValue.childProductIds.includes(pid)
    );
    const addProducts = formValue.childProductIds.filter(
      (pid) => !this.initialchildProductIds.includes(pid)
    );

    this.store
      .dispatch(
        this.mode === ModalMode.Create
          ? new AddProductAction(product, false)
          : new UpdateProductAction(product, false)
      )
      .pipe(
        delay(300),
        switchMap(() =>
          forkJoin([
            this.store.dispatch(
              new RemoveChildProducts(product.id, removeProducts)
            ),
            this.store.dispatch(new AddChildProducts(product.id, addProducts)),
          ])
        ),
        delay(200),
        switchMap(() => this.store.dispatch(new LoadProductAction(product.id)))
      )
      .subscribe({
        next: () => {
          if (this.mode === ModalMode.Create) {
            this.router.navigate(['..', this.form.getRawValue().id], {
              relativeTo: this.route,
            });
          } else {
            this.router.navigate(['..'], { relativeTo: this.route });
          }
          dialogRef.close();
        },
        error: () => {
          dialogRef.close();
        },
      });
  }

  identFormatter = (selected: Ident) => {
    const ident = this.idents().find((ident) => ident.ident === selected.ident);
    return ident ? `[${ident.ident}] ${ident.name}` : '';
  };

  identResultFormatter = (ident: Ident) => `[${ident.ident}] ${ident.name}`;

  formatIdentLabel(subject: Ident) {
    return `[${subject.ident}] ${subject.name}`;
  }

  public onFocusTypeahead(e: Event): void {
    e.stopPropagation();
    setTimeout(() => {
      const inputEvent: Event = new Event('input');
      e.target.dispatchEvent(inputEvent);
    }, 0);
  }

  filterIdents: OperatorFunction<string, readonly Ident[]> = (
    text$: Observable<string>
  ) =>
    text$.pipe(
      debounceTime(200),
      map((text: string) =>
        this.idents().filter((ident) =>
          this.formatIdentLabel(ident)
            .toLowerCase()
            .includes((text + '').toLowerCase())
        )
      )
    );

  checkForChanges(e) {
    if (this.valuesChanged) {
      this.confirmService
        .confirm({
          title: this.translateService.instant(
            'configurationPage.products.form.confirm.unsavedChangesTitle'
          ),
          message: this.translateService.instant(
            'configurationPage.products.form.confirm.leaveMessage'
          ),
        })
        .subscribe((result) => {
          if (result.closeReason === ConfirmCloseReason.Yes) {
            this.router.navigate(['..'], { relativeTo: this.route });
          }
        });
    } else {
      this.router.navigate(['..'], { relativeTo: this.route });
    }
  }

  protected readonly AnalysisType = AnalysisType;
  protected readonly DataInputType = DataInputType;
  protected readonly Gender = Gender;
  protected readonly IncidentalFindingsReporting = IncidentalFindingsReporting;
  protected readonly ProductAdditionalFields = ProductAdditionalFields;
  protected readonly sampleTypeKey = sampleTypeKey;

  addAnalysisProductCode() {
    const lab = this.analysisProductCodeLab.value;
    const code = this.analysisProductCodeValue.value?.trim();

    if (!lab || !code) {
      return;
    }

    const current = (this.form.get('analysisProductCodes').value ||
      []) as ProductAnalysisCodeModel[];
    const existingIndex = current.findIndex(
      (entry) => entry.laboratory === lab
    );
    const next = [...current];

    if (existingIndex >= 0) {
      next[existingIndex] = { laboratory: lab, code };
    } else {
      next.push({ laboratory: lab, code });
    }

    this.form.get('analysisProductCodes').setValue(next);

    this.analysisProductCodeLab.setValue('');
    this.analysisProductCodeValue.setValue('');
    this.analysisProductCodeOptions.set([]);
  }

  removeAnalysisProductCode(lab: string) {
    const current = (this.form.get('analysisProductCodes').value ||
      []) as ProductAnalysisCodeModel[];
    const next = current.filter((entry) => entry.laboratory !== lab);
    this.form.get('analysisProductCodes').setValue(next);
  }

  getAnalysisProductCodeEntries(): ProductAnalysisCodeModel[] {
    return (this.form.get('analysisProductCodes').value ||
      []) as ProductAnalysisCodeModel[];
  }

  private getAvailableLaboratories(): string[] {
    const analysisProductCodes = (this.form.get('analysisProductCodes').value ||
      []) as ProductAnalysisCodeModel[];
    const used = new Set(analysisProductCodes.map((entry) => entry.laboratory));
    return this.laboratories.filter((lab) => !used.has(lab));
  }
}
export enum ProductAdditionalFields {
  IncidentalFindings = 'incidentalFindingsReporting',
  Sex = 'sex',
  ProductSubTypes = 'productSubTypes',
  SampleTypes = 'allowedSampleTypes',
  RequirePatientAddress = 'requirePatientAddress',
}
